import type { License } from '@/types/license.types';

import { LocalesEnum } from '@/enums/locales.enum';
import { UserRoleEnum } from '@/enums/global.enum';

import apiService from '@/services/api.service';
import { usePostHog } from '@/composables/usePostHog';
import { defineStore } from 'pinia';
import licensesService from '@/services/licenses.service';

export const useUserStore = defineStore(
    'userStore',
    () => {
        const session = ref();
        const accounts = ref<{ id: string; name: string }[]>([]);
        const licenses = ref<License[]>();

        const { posthog } = usePostHog();

        const sessionId = computed(() => session.value?.id ?? '');
        const user = computed(() => session.value?.user);
        const account = computed(() => session.value?.account);
        const organizationId = computed(() => session.value?.organization?.id);
        const types = computed(() => session.value?.organization?.types);
        const type = computed(() => session.value?.account?.type ?? UserRoleEnum.Client);
        const isNotary = computed(() => session.value?.account?.type === 'notary');
        const isSupport = computed(() => session.value?.account?.type === 'support');
        const isSuperAdmin = computed(() => session.value?.account?.type === 'support' && session.value?.role?.id === 'admin');
        const isClient = computed(() => session.value?.account?.type === 'client');
        const isAdvisor = computed(() => session.value?.account?.type === 'bookkeeper');
        const isAdmin = computed(() => isNotary.value || isSupport.value);

        async function updateSessionFromApi() {
            const { status, data } = await apiService.get(`sessions/${session.value.id}`);

            if (status === 200) {
                session.value = data;

                identifyPosthogUser();

                licenses.value = await licensesService.getLicenses();
            }
        }

        async function setCurrentAccount(id) {
            const { status, data } = await apiService.post(`sessions/${session.value.id}`, { account: id });

            if (status === 201) {
                session.value = data;

                identifyPosthogUser();

                licenses.value = await licensesService.getLicenses();
            }
        }

        async function updateAccounts() {
            if (!session.value?.id || accounts.value?.length > 0) return;

            const { data } = await apiService.get('accounts', [{ key: 'personal', value: 'true' }]);

            accounts.value = data.map((account) => ({ name: account.name, id: account.id }));
            licenses.value = await licensesService.getLicenses();
        }

        function updateSession(value) {
            session.value = value;

            identifyPosthogUser();
        }

        function updateUser(value) {
            session.value ||= {};
            session.value.user = value;

            identifyPosthogUser();
        }

        function updateUserLocale(locale: LocalesEnum) {
            if (session.value.user) session.value.user.locale = locale;
        }

        function cleanSession() {
            session.value = {};
            posthog.reset();
        }

        function updateLicenses(newLicenses: License[]) {
            licenses.value = newLicenses;
        }

        function identifyPosthogUser() {
            if (!session.value.user) return;

            const userId = session.value.user.id;
            const posthogDistinctId = posthog.get_distinct_id();

            // Never reset() here: identify() sends the current (anonymous) distinct id as
            // `$anon_distinct_id`, which merges the pre-registration person into this user.
            // reset() would sever that link — it belongs on logout only (`cleanSession`).
            // The guard only avoids a redundant `$set` on every page load for a user who is
            // already identified; identify() itself is a no-op when the ids are equal.
            if (posthogDistinctId !== userId) {
                posthog.identify(userId, {
                    email: session.value.user.email,
                    name: `${session.value.user.first_name} ${session.value.user.last_name}`,
                });
            }
        }

        return {
            account,
            accounts,
            isAdmin,
            isAdvisor,
            isClient,
            isNotary,
            isSupport,
            isSuperAdmin,
            licenses,
            organizationId,
            session,
            sessionId,
            type,
            types,
            user,

            cleanSession,
            setCurrentAccount,
            updateAccounts,
            updateLicenses,
            updateSession,
            updateSessionFromApi,
            updateUser,
            updateUserLocale,
        };
    },
    {
        persistedState: {
            persist: false,
        },
    },
);
